import { Link, useNavigate } from 'react-router-dom'
import { Users, Swords, Briefcase, Star } from 'lucide-react'
import type { Account, Contact, RelationshipTrack, Stance } from '@/types/entities'
import {
  SectionCard, EmptyState, Avatar, StanceBadge, Badge,
  DataTable, ViewToggle, useViewMode, type Column,
} from '@/components/ui'
import { contactName } from '@/lib/selectors'
import { useStore } from '@/store/useStore'
import type { AccountDerived } from '../lib'

const INFL_RANK: Record<string, number> = { High: 0, Medium: 1, Low: 2 }
const isDecisionMaker = (role?: string) => !!role && /decision maker/i.test(role)
const telHref = (p?: string) => (p ? `tel:${p.replace(/[^+\d]/g, '')}` : undefined)

interface MemberRow { c: Contact; role?: string; stance?: Stance; isPrimary: boolean; track?: RelationshipTrack }

export default function ContactsTab({ account, d }: { account: Account; d: AccountDerived }) {
  const navigate = useNavigate()
  const patch = useStore((s) => s.patch)
  const [viewMode, setViewMode] = useViewMode('account-contacts', 'cards')
  const contacts = d.contacts

  if (!contacts.length && d.tracks.every((t) => !t.wpiTeam.length)) {
    return (
      <SectionCard pad>
        <EmptyState icon={<Users size={36} />} title="No contacts on this account" hint="Counterparty and WPI team contacts will appear here once associated with the relationship." />
      </SectionCard>
    )
  }

  const isWpiHome = account.type === 'Personal'
  const toolbar = (
    <div className="aw-tab-toolbar">
      <ViewToggle mode={viewMode} onChange={setViewMode} />
    </div>
  )

  function setPrimary(track: RelationshipTrack, contactId: string) {
    patch('tracks', track.id, { primaryContactId: contactId })
    if (d.tracks.length === 1) patch('accounts', account.id, { primaryContactId: contactId })
  }

  /* ---------------- WPI internal-team home ---------------- */
  if (isWpiHome) {
    const sorted = [...contacts].sort((a, b) => contactName(a).localeCompare(contactName(b)))
    const wpiCols: Column<Contact>[] = [
      { key: 'name', header: 'Name', sortValue: (c) => `${c.lastName} ${c.firstName}`.toLowerCase(),
        render: (c) => <NameCell c={c} /> },
      { key: 'dept', header: 'Department', sortValue: (c) => c.department ?? '', render: (c) => c.department ?? <span className="dim">—</span> },
      { key: 'title', header: 'Title', sortValue: (c) => c.title ?? '', render: (c) => c.title ?? <span className="dim">—</span> },
      { key: 'email', header: 'Email', render: (c) => c.workEmail
          ? <a href={`mailto:${c.workEmail}`} onClick={(e) => e.stopPropagation()}>{c.workEmail}</a> : <span className="dim">—</span> },
    ]
    const byDept = new Map<string, Contact[]>()
    for (const c of contacts) { const k = c.department ?? 'Other'; (byDept.get(k) ?? byDept.set(k, []).get(k)!).push(c) }
    return (
      <div className="aw-stack">
        {toolbar}
        {viewMode === 'rows' ? (
          <SectionCard pad={false}><DataTable<Contact> columns={wpiCols} rows={sorted} getRowKey={(c) => c.id} onRowClick={(c) => navigate(`/contacts/${c.id}`)} initialSort={{ key: 'dept', dir: 'asc' }} /></SectionCard>
        ) : (
          [...byDept.entries()].map(([dpt, list]) => (
            <SectionCard key={dpt} eyebrow="WPI Team" title={dpt} right={<span className="aw-count-pill">{list.length}</span>}>
              <div className="aw-contact-grid">{list.map((c) => <ContactCard key={c.id} c={c} onClick={() => navigate(`/contacts/${c.id}`)} />)}</div>
            </SectionCard>
          ))
        )}
      </div>
    )
  }

  /* ---------------- Counterparty / track view ---------------- */
  const membersInTrack = (track: RelationshipTrack): MemberRow[] => {
    const primaryId = track.primaryContactId ?? (d.tracks.length === 1 ? account.primaryContactId : undefined)
    return track.counterpartyContactIds
      .map((id) => contacts.find((c) => c.id === id))
      .filter((c): c is Contact => !!c)
      .map((c) => {
        const tm = (c.trackMemberships ?? []).find((m) => m.trackId === track.id)
        return { c, role: tm?.role, stance: tm?.stance ?? c.stance, isPrimary: c.id === primaryId, track }
      })
      .sort((a, b) => {
        const ra = a.isPrimary ? 0 : isDecisionMaker(a.role) ? 1 : 2
        const rb = b.isPrimary ? 0 : isDecisionMaker(b.role) ? 1 : 2
        if (ra !== rb) return ra - rb
        const ia = INFL_RANK[a.c.influence ?? ''] ?? 3
        const ib = INFL_RANK[b.c.influence ?? ''] ?? 3
        if (ia !== ib) return ia - ib
        return contactName(a.c).localeCompare(contactName(b.c))
      })
  }

  const grouped = d.tracks.map((t) => ({ track: t, members: membersInTrack(t) }))
  const placedIds = new Set(grouped.flatMap((g) => g.members.map((m) => m.c.id)))
  const unplaced = contacts.filter((c) => !placedIds.has(c.id) && !c.isWpi)
  const competing = d.tracks.length > 1 && d.tracks.some((t) => (t.competesWithTrackIds?.length ?? 0) > 0)
  const multiTrack = d.tracks.length > 1

  const banner = competing ? (
    <div className="aw-competing-banner">
      <Swords size={15} />
      <span>This account spans <b>two internally competing tracks</b>. Counterparties are grouped by track; the same firm sits on both sides of the table.</span>
    </div>
  ) : null

  /* ---- rows mode: one table across all counterparty contacts ---- */
  if (viewMode === 'rows') {
    const flat: MemberRow[] = [
      ...grouped.flatMap((g) => g.members),
      ...unplaced.map((c) => ({ c, role: c.title, stance: c.stance, isPrimary: false, track: undefined })),
    ]
    const cols: Column<MemberRow>[] = [
      { key: 'name', header: 'Name', sortValue: (r) => `${r.c.lastName} ${r.c.firstName}`.toLowerCase(), render: (r) => <NameCell c={r.c} primary={r.isPrimary} /> },
      { key: 'role', header: 'Role', sortValue: (r) => (r.isPrimary ? 0 : isDecisionMaker(r.role) ? 1 : 2),
        render: (r) => r.isPrimary
          ? <Badge tone="info" variant="solid"><Star size={10} /> Primary</Badge>
          : isDecisionMaker(r.role) ? <Badge tone="navy" variant="outline">Decision Maker</Badge>
          : <span className="muted">{r.role ?? '—'}</span> },
      ...(multiTrack ? [{ key: 'track', header: 'Track', sortValue: (r: MemberRow) => r.track?.name ?? '', render: (r: MemberRow) => r.track ? <span className="muted">{r.track.name}</span> : <span className="dim">—</span> }] : []),
      { key: 'stance', header: 'Stance', align: 'center' as const, sortValue: (r) => r.stance ?? 'zzz', render: (r) => r.stance ? <StanceBadge stance={r.stance} /> : <span className="dim">—</span> },
      { key: 'influence', header: 'Influence', sortValue: (r) => INFL_RANK[r.c.influence ?? ''] ?? 3, render: (r) => r.c.influence ? <Badge tone="neutral" variant="outline">{r.c.influence}</Badge> : <span className="dim">—</span> },
      { key: 'contact', header: 'Contact', render: (r) => {
          const tel = r.c.cellPhone ?? r.c.officePhone
          if (!r.c.workEmail && !tel) return <span className="dim">—</span>
          return <span className="aw-row-contact">
            {r.c.workEmail && <a href={`mailto:${r.c.workEmail}`} onClick={(e) => e.stopPropagation()}>{r.c.workEmail}</a>}
            {tel && <a href={telHref(tel)} onClick={(e) => e.stopPropagation()}>{tel}</a>}
          </span> } },
      { key: 'star', header: '', width: 48, align: 'center' as const, render: (r) => r.track && !r.isPrimary
          ? <button className="aw-setprimary" title="Set as primary contact" onClick={(e) => { e.stopPropagation(); setPrimary(r.track!, r.c.id) }}><Star size={14} /></button>
          : null },
    ]
    return (
      <div className="aw-stack">
        {banner}
        {toolbar}
        <SectionCard pad={false}>
          <DataTable<MemberRow> columns={cols} rows={flat} getRowKey={(r) => r.c.id} onRowClick={(r) => navigate(`/contacts/${r.c.id}`)} initialSort={{ key: 'role', dir: 'asc' }} emptyText="No counterparty contacts." />
        </SectionCard>
      </div>
    )
  }

  /* ---- cards mode: relationship map grouped by track ---- */
  return (
    <div className="aw-stack">
      {banner}
      {toolbar}
      {grouped.map(({ track, members }) => {
        const primary = members.find((m) => m.isPrimary)
        const dms = members.filter((m) => !m.isPrimary && isDecisionMaker(m.role))
        return (
          <SectionCard key={track.id} eyebrow={track.type} title={track.name}
            right={<div className="aw-track-tags">{track.module && <Badge tone="navy" variant="outline">{track.module}</Badge>}<span className="aw-count-pill">{members.length}</span></div>}>
            {(primary || dms.length > 0) && (
              <div className="aw-relmap">
                <span className="aw-relmap-item"><span className="label">Primary</span><b>{primary ? contactName(primary.c) : '—'}</b></span>
                <span className="aw-relmap-sep" />
                <span className="aw-relmap-item"><span className="label">Decision {dms.length === 1 ? 'Maker' : 'Makers'}</span><b>{dms.length ? dms.map((m) => contactName(m.c)).join(', ') : '—'}</b></span>
              </div>
            )}
            {members.length ? (
              <div className="aw-contact-grid">
                {members.map(({ c, role, stance, isPrimary }) => (
                  <ContactCard key={c.id} c={c} role={role} stance={stance} isPrimary={isPrimary}
                    onClick={() => navigate(`/contacts/${c.id}`)}
                    onSetPrimary={isPrimary ? undefined : () => setPrimary(track, c.id)} />
                ))}
              </div>
            ) : <div className="aw-empty-inline">No counterparty contacts mapped to this track.</div>}

            {track.wpiTeam.length > 0 && (
              <div className="aw-wpi-team">
                <div className="label aw-wpi-team-label"><Briefcase size={12} /> WPI Team</div>
                <div className="aw-wpi-chips">
                  {track.wpiTeam.map((m) => (
                    <span key={m.name} className="aw-wpi-chip">
                      <Avatar name={m.name} size={22} tone="slate" />
                      <span className="aw-wpi-chip-text"><b>{m.name}</b><span>{m.role}</span></span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>
        )
      })}

      {unplaced.length > 0 && (
        <SectionCard eyebrow="Other" title="Additional Contacts" right={<span className="aw-count-pill">{unplaced.length}</span>}>
          <div className="aw-contact-grid">{unplaced.map((c) => <ContactCard key={c.id} c={c} role={c.title} stance={c.stance} onClick={() => navigate(`/contacts/${c.id}`)} />)}</div>
        </SectionCard>
      )}
    </div>
  )
}

function NameCell({ c, primary }: { c: Contact; primary?: boolean }) {
  return (
    <span className="aw-namecell">
      <Avatar name={contactName(c)} size={30} tone={primary ? 'cyan' : 'navy'} />
      <span className="aw-namecell-text">
        <Link to={`/contacts/${c.id}`} className="aw-namecell-link truncate" onClick={(e) => e.stopPropagation()}>{contactName(c)}</Link>
        {c.title && <span className="aw-namecell-sub truncate">{c.title}</span>}
      </span>
    </span>
  )
}

function ContactCard({
  c, role, stance, isPrimary, onClick, onSetPrimary,
}: {
  c: Contact; role?: string; stance?: Contact['stance']; isPrimary?: boolean
  onClick: () => void; onSetPrimary?: () => void
}) {
  const decisionMaker = isDecisionMaker(role)
  return (
    <div className={`aw-contact-card${isPrimary ? ' aw-contact-card--primary' : ''}`}
      role="button" tabIndex={0} onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}>
      <Avatar name={contactName(c)} size={38} tone={isPrimary ? 'cyan' : 'navy'} />
      <div className="aw-contact-info">
        <div className="aw-contact-name">{contactName(c)}</div>
        <div className="aw-contact-title truncate">{role ?? c.title ?? '—'}</div>
        <div className="aw-contact-badges">
          {isPrimary && <Badge tone="info" variant="solid"><Star size={10} /> Primary</Badge>}
          {decisionMaker && !isPrimary && <Badge tone="navy" variant="outline">Decision Maker</Badge>}
          {stance && <StanceBadge stance={stance} />}
          {c.influence && <Badge tone="neutral" variant="outline">{c.influence} influence</Badge>}
        </div>
      </div>
      {onSetPrimary && (
        <button className="aw-setprimary" title="Set as primary contact" onClick={(e) => { e.stopPropagation(); onSetPrimary() }}><Star size={14} /></button>
      )}
    </div>
  )
}
